/**
 * Motor de sincronizacion (spec/02-architecture.md §4 F3; ADR OQ-1 c.1):
 * anexa el outbox a Sheets de forma idempotente, con comprobacion previa y
 * verificacion posterior — nunca confia ciegamente en la respuesta de la API.
 */

import type { ChainedEntry } from "../integrity/chain.js";
import type { Outbox } from "../storage/outbox.js";
import { withExponentialBackoff, type BackoffOptions, type SheetRow, type SheetsAdapter } from "./sheets-adapter.js";

export type EntryLookup = (entryId: string) => Promise<ChainedEntry | undefined>;

export interface SyncResult {
  readonly acknowledged: readonly string[];
  readonly stillPending: readonly string[];
  readonly missingLookup: readonly string[];
}

export function toSheetRow(chained: ChainedEntry): SheetRow {
  return {
    entryId: chained.entry.entryId,
    deviceId: chained.deviceId,
    deviceSeq: chained.deviceSeq,
    date: chained.entry.date,
    description: chained.entry.description,
    origin: chained.entry.origin,
    reversesEntryId: chained.entry.reversesEntryId,
    createdAt: chained.createdAt,
    postingsJson: JSON.stringify(
      chained.entry.postings.map((p) => ({
        ...p,
        amountMinor: p.amountMinor.toString(),
        quantityMinor: p.quantityMinor === null ? null : p.quantityMinor.toString(),
      })),
    ),
    entryHash: chained.entryHash,
    deviceChainHash: chained.deviceChainHash,
  };
}

export async function syncOutbox(
  outbox: Outbox,
  adapter: SheetsAdapter,
  lookupChainedEntry: EntryLookup,
  backoffOptions?: BackoffOptions,
): Promise<SyncResult> {
  const pending = (await outbox.listPending()).filter((item) => item.kind === "entry");

  const toSend: ChainedEntry[] = [];
  const missingLookup: string[] = [];
  for (const item of pending) {
    const chained = await lookupChainedEntry(item.id);
    if (chained) {
      toSend.push(chained);
    } else {
      missingLookup.push(item.id);
    }
  }

  if (toSend.length === 0) {
    return { acknowledged: [], stillPending: pending.map((p) => p.id), missingLookup };
  }

  // Paso 1: comprobar que identidades ya existen (idempotencia, ADR OQ-1 c.1).
  const existingBefore = await withExponentialBackoff(() => adapter.listExistingEntryIds(), backoffOptions);
  const rowsToAppend = toSend.filter((c) => !existingBefore.has(c.entry.entryId)).map(toSheetRow);

  if (rowsToAppend.length > 0) {
    await withExponentialBackoff(() => adapter.appendEntries(rowsToAppend), backoffOptions);
  }

  // Paso 2: verificar despues de anexar — nunca se confia solo en que la llamada no lanzara error.
  const existingAfter = await withExponentialBackoff(() => adapter.listExistingEntryIds(), backoffOptions);

  const acknowledged: string[] = [];
  const stillPending: string[] = [];
  for (const chained of toSend) {
    if (existingAfter.has(chained.entry.entryId)) {
      await outbox.acknowledge(chained.entry.entryId);
      acknowledged.push(chained.entry.entryId);
    } else {
      await outbox.recordAttemptFailure(chained.entry.entryId, "no confirmado tras el anexado");
      stillPending.push(chained.entry.entryId);
    }
  }

  return { acknowledged, stillPending, missingLookup };
}
